@if (enabled) {
<section class="php-cat">
  <div class="sw-tpl-intro php-cat-intro">
    <div class="php-cat-intro-text">
      <div class="sw-tpl-intro-title">Incident categories</div>
      <div class="sw-tpl-intro-subtitle">
        From the client portal. These decide what its report form asks. Edits are saved there.
      </div>
    </div>
    <button type="button" class="php-cat-add-btn" (click)="openCreate()" [disabled]="isOpeningNew || isOpening !== null">
      @if (isOpeningNew) {
      <span class="spinner-border spinner-border-sm"></span>
      } @else {
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      }
      Add category
    </button>
  </div>

  @if (isLoading) {
  <div class="sw-tpl-grid">
    @for (s of [0,1,2,3]; track s) {
    <div class="sw-tpl-card">
      <div class="sw-tpl-card-head">
        <app-skeleton width="55%" height="18px"></app-skeleton>
        <app-skeleton width="80%" height="12px" margin="0.5rem 0 0"></app-skeleton>
      </div>
      <div class="sw-tpl-footer">
        <app-skeleton width="100%" height="36px" borderRadius="8px"></app-skeleton>
      </div>
    </div>
    }
  </div>
  } @else if (loadFailed) {
  <div class="php-cat-note">
    Could not reach the client portal.
    <button type="button" class="sw-tpl-ghost-btn php-cat-retry" (click)="load()">Try again</button>
  </div>
  } @else if (categories.length === 0) {
  <div class="php-cat-note">The client portal has no incident categories yet.</div>
  } @else {
  <div class="sw-tpl-grid">
    @for (c of categories; track c.id) {
    <div class="sw-tpl-card">
      <div class="sw-tpl-card-head">
        <div class="sw-tpl-card-titlerow">
          <div class="sw-form-card-headleft">
            <span class="sw-form-card-icon php-cat-icon" [style.--kc]="c.colour || '#6b6263'" [innerHTML]="icon(iconMap[c.icon])"></span>
            <div class="sw-tpl-card-name">{{ c.name }}</div>
          </div>
          <span class="sw-tpl-badge" [class.php-cat-badge--off]="!c.offered">
            {{ c.offered ? 'On the form' : 'Retired' }}
          </span>
        </div>
        <div class="sw-tpl-card-desc">{{ describe(c) }}</div>
      </div>

      <div class="sw-form-card-body">
        <span class="sw-form-meta-pill">{{ c.code }}</span>
        <span class="sw-form-meta-pill sw-form-meta-pill--muted">{{ c.reports }} report{{ c.reports === 1 ? '' : 's' }}</span>
        @if (c.asks.length) {
        <span class="sw-form-meta-pill sw-form-meta-pill--muted">Asks: {{ c.asks.join(', ') }}</span>
        }
      </div>

      <div class="sw-tpl-footer">
        <button type="button" class="sw-tpl-primary-btn" (click)="openEdit(c)" [disabled]="isOpening !== null">
          @if (isOpening === c.id) { <span class="spinner-border spinner-border-sm"></span> }
          Edit category
        </button>
        <button type="button" class="sw-tpl-ghost-btn sw-tpl-ghost-btn--danger" (click)="deleteCategory(c)"
          [disabled]="isDeleting !== null" title="Delete category" aria-label="Delete category">
          @if (isDeleting === c.id) {
          <span class="spinner-border spinner-border-sm"></span>
          } @else {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          }
        </button>
      </div>
    </div>
    }
  </div>
  }
</section>

<sw-drawer [visible]="panelOpen" (visibleChange)="$event ? null : closePanel()"
  [title]="isNew ? 'New incident category' : editing ? 'Editing ' + editing.name : 'Edit category'" width="min(72rem, 100vw)" (hide)="closePanel()">
  <div drawer-body>
    @if (draft && catalog && editing) {
    <div class="php-cat-preview" [style.--kc]="draft.colour || '#6b6263'">
      <span class="php-cat-preview-icon" [innerHTML]="icon(iconSvgOf(draft.icon))"></span>
      <div class="php-cat-preview-text">
        <div class="php-cat-preview-name">{{ draft.name || 'Untitled category' }}</div>
        <div class="php-cat-preview-sub">{{ draft.subtitle || 'The card shows the first three “What happened” options' }}</div>
      </div>
      @if (isNew) {
      <span class="php-cat-preview-count">New</span>
      } @else {
      <span class="php-cat-preview-code">{{ editing.code }}</span>
      <span class="php-cat-preview-count">{{ editing.reports }} report{{ editing.reports === 1 ? '' : 's' }}</span>
      }
    </div>

    <div class="php-cat-form">

      <!-- 1 · Basics -->
      <div class="php-cat-block php-cat-span2">
        <div class="php-cat-title"><span>1</span> Basics <small>How the category looks on the report form</small></div>
        <div class="php-cat-basics">
        <div class="php-cat-basics-main">
        <div class="php-cat-row">
          <div class="php-cat-field php-cat-grow">
            <label class="php-cat-label">Category name <i>*</i></label>
            <input type="text" class="php-cat-input" maxlength="120" [(ngModel)]="draft.name" />
          </div>
          <div class="php-cat-field php-cat-order">
            <label class="php-cat-label">Order</label>
            <input type="number" class="php-cat-input" min="0" max="999" step="10" [(ngModel)]="draft.sort_order" />
          </div>
        </div>
        <div class="php-cat-field">
          <label class="php-cat-label">Subtitle on the card</label>
          <input type="text" class="php-cat-input" maxlength="160" [(ngModel)]="draft.subtitle" />
          <div class="php-cat-hint">Blank shows the first three “What happened” options.
            @if (isNew) {
            The code is made from the name when you save and never changes after that.
            } @else {
            Code <b>{{ editing.code }}</b> is fixed; renaming is safe.
            }</div>
        </div>
        </div>
        <div class="php-cat-basics-look">
        <div class="php-cat-field">
          <label class="php-cat-label">Icon</label>
          <div class="php-cat-picks">
            @for (i of catalog.icons; track i.key) {
            <button type="button" class="php-cat-pick" [class.on]="draft.icon === i.key" (click)="draft.icon = i.key"
              [attr.aria-pressed]="draft.icon === i.key" [attr.aria-label]="i.key">
              <span class="php-cat-svg" [innerHTML]="icon(i.svg)"></span>
            </button>
            }
          </div>
        </div>
        <div class="php-cat-field">
          <label class="php-cat-label">Colour</label>
          <div class="php-cat-picks">
            @for (c of catalog.colours; track c.hex) {
            <button type="button" class="php-cat-swatch" [class.on]="draft.colour.toUpperCase() === c.hex.toUpperCase()"
              [style.background]="c.hex" (click)="draft.colour = c.hex" [attr.aria-label]="c.name" [title]="c.name"></button>
            }
          </div>
        </div>
        </div>
        </div>
      </div>

      <!-- 2 · What happened -->
      <div class="php-cat-block php-cat-span2">
        <div class="php-cat-title"><span>2</span> What happened <small>The answers this category offers</small></div>
        <div class="php-cat-field">
          <label class="php-cat-label">Options <i>*</i></label>
          <sw-select [multiple]="true" [options]="catalog.events" optionLabel="name" optionValue="code"
            [ngModel]="draft.events" (ngModelChange)="onEventsChange($event)" placeholder="Pick the answers"
            appendTo="body"></sw-select>
        </div>
        @if (draft.newevents.length) {
        <div class="php-cat-chips">
          @for (n of draft.newevents; track n; let i = $index) {
          <span class="php-cat-chip">{{ n }} <button type="button" (click)="remove(draft.newevents, i)"
              aria-label="Remove">×</button></span>
          }
        </div>
        }
        <div class="php-cat-add">
          <input type="text" class="php-cat-input" maxlength="120" placeholder="Add another option, e.g. Blocked fire lane"
            [(ngModel)]="newEventText" (keydown.enter)="$event.preventDefault(); addEvent()" />
          <button type="button" class="php-cat-addbtn" (click)="addEvent()">Add option</button>
        </div>
      </div>

      <!-- 3 · Results -->
      <div class="php-cat-block php-cat-span2">
        <div class="php-cat-title"><span>3</span> Results <small>How each answer can end — at least one each</small></div>
        @for (code of draft.events; track code) {
        <div class="php-cat-result">
          <div class="php-cat-result-row">
            <div class="php-cat-result-name">{{ eventName(code) }}</div>
            <sw-select [multiple]="true" [options]="catalog.outcomes" optionLabel="name" optionValue="code"
              [ngModel]="draft.evout[code] || []" (ngModelChange)="draft.evout[code] = $event"
              placeholder="Pick the results" appendTo="body"></sw-select>
          </div>
          @if (draft.evnew[code]?.length) {
          <div class="php-cat-chips">
            @for (n of draft.evnew[code]; track n; let i = $index) {
            <span class="php-cat-chip">{{ n }} <button type="button" (click)="remove(draft.evnew[code], i)"
                aria-label="Remove">×</button></span>
            }
          </div>
          }
          <div class="php-cat-add php-cat-add--inset">
            <input type="text" class="php-cat-input" maxlength="160" placeholder="Add a result for this answer"
              [(ngModel)]="newResultText[code]" (keydown.enter)="$event.preventDefault(); addResult(code)" />
            <button type="button" class="php-cat-addbtn" (click)="addResult(code)">Add</button>
          </div>
        </div>
        }
        @if (!draft.events.length) {
        <div class="php-cat-hint">Pick the “What happened” options first.</div>
        }
      </div>

      <!-- 4 · How he gained access (Unauthorized only) -->
      @if (asksHow) {
      <div class="php-cat-block">
        <div class="php-cat-title"><span>4</span> How he gained access <small>Shared list — unticked options are retired</small></div>
        <div class="php-cat-field">
          <label class="php-cat-label">Options <i>*</i></label>
          <sw-select [multiple]="true" [options]="catalog.hows" optionLabel="name" optionValue="code"
            [(ngModel)]="draft.hows" placeholder="Pick the ways in" appendTo="body"></sw-select>
        </div>
        @if (draft.newhows.length) {
        <div class="php-cat-chips">
          @for (n of draft.newhows; track n; let i = $index) {
          <span class="php-cat-chip">{{ n }} <button type="button" (click)="remove(draft.newhows, i)"
              aria-label="Remove">×</button></span>
          }
        </div>
        }
        <div class="php-cat-add">
          <input type="text" class="php-cat-input" maxlength="120" placeholder="Add another way in, e.g. Climbed a wall"
            [(ngModel)]="newHowText" (keydown.enter)="$event.preventDefault(); addHow()" />
          <button type="button" class="php-cat-addbtn" (click)="addHow()">Add option</button>
        </div>
      </div>
      }

      <!-- Action taken -->
      <div class="php-cat-block">
        <div class="php-cat-title"><span>{{ asksHow ? 5 : 4 }}</span> Action taken <small>What the operator did — several can be picked</small></div>
        <label class="php-cat-check">
          <input type="checkbox" [(ngModel)]="draft.asks_action" />
          <span>Ask “Action taken” for this category</span>
        </label>
        <div class="php-cat-field">
          <label class="php-cat-label">Options <small>shared by every category that asks it</small></label>
          <sw-select [multiple]="true" [options]="catalog.actions" optionLabel="name" optionValue="code"
            [(ngModel)]="draft.acts" placeholder="Pick the actions" appendTo="body"></sw-select>
        </div>
        @if (draft.newacts.length) {
        <div class="php-cat-chips">
          @for (n of draft.newacts; track n; let i = $index) {
          <span class="php-cat-chip">{{ n }} <button type="button" (click)="remove(draft.newacts, i)"
              aria-label="Remove">×</button></span>
          }
        </div>
        }
        <div class="php-cat-add">
          <input type="text" class="php-cat-input" maxlength="120" placeholder="Add another action, e.g. Fire department contacted"
            [(ngModel)]="newActText" (keydown.enter)="$event.preventDefault(); addAct()" />
          <button type="button" class="php-cat-addbtn" (click)="addAct()">Add option</button>
        </div>
      </div>

      <!-- Response reason -->
      <div class="php-cat-block">
        <div class="php-cat-title"><span>{{ asksHow ? 6 : 5 }}</span> Response reason <small>What brought us out — one is picked</small></div>
        <label class="php-cat-check">
          <input type="checkbox" [(ngModel)]="draft.asks_response" />
          <span>Ask “Response reason” for this category</span>
        </label>
        <div class="php-cat-field">
          <label class="php-cat-label">Options <small>shared by every category that asks it</small></label>
          <sw-select [multiple]="true" [options]="catalog.responses" optionLabel="name" optionValue="code"
            [(ngModel)]="draft.resps" placeholder="Pick the reasons" appendTo="body"></sw-select>
        </div>
        @if (draft.newresps.length) {
        <div class="php-cat-chips">
          @for (n of draft.newresps; track n; let i = $index) {
          <span class="php-cat-chip">{{ n }} <button type="button" (click)="remove(draft.newresps, i)"
              aria-label="Remove">×</button></span>
          }
        </div>
        }
        <div class="php-cat-add">
          <input type="text" class="php-cat-input" maxlength="120" placeholder="Add another reason, e.g. Neighbour called"
            [(ngModel)]="newRespText" (keydown.enter)="$event.preventDefault(); addResp()" />
          <button type="button" class="php-cat-addbtn" (click)="addResp()">Add option</button>
        </div>
      </div>

      <!-- Extra questions -->
      <div class="php-cat-block" [class.php-cat-span2]="!asksHow">
        <div class="php-cat-title"><span>{{ asksHow ? 7 : 6 }}</span> Extra questions <small>Asked only for this category</small></div>
        <div class="php-cat-checks">
          <label class="php-cat-check"><input type="checkbox" [(ngModel)]="draft.asks_tenant" />
            <span>Ask “Tenant associated with this incident?”</span></label>
          <label class="php-cat-check"><input type="checkbox" [(ngModel)]="draft.asks_taken" />
            <span>Ask “Was the tenant taken?”</span></label>
          <label class="php-cat-check"><input type="checkbox" [(ngModel)]="draft.tracks_violation" />
            <span>Feed the Tenant violation notice tab</span></label>
        </div>
        @if (catalog.questions.length) {
        <div class="php-cat-field">
          <label class="php-cat-label">Your own questions</label>
          <sw-select [multiple]="true" [options]="catalog.questions" optionLabel="label" optionValue="code"
            [(ngModel)]="draft.questions" placeholder="Pick the questions" appendTo="body"></sw-select>
        </div>
        }
        @if (draft.newQuestions.length) {
        <div class="php-cat-chips">
          @for (q of draft.newQuestions; track $index; let i = $index) {
          <span class="php-cat-chip">{{ q.label }} <button type="button" (click)="draft.newQuestions.splice(i, 1)"
              aria-label="Remove">×</button></span>
          }
        </div>
        }
        <div class="php-cat-newq">
          <input type="text" class="php-cat-input" maxlength="160" placeholder="New question, e.g. Was a vehicle involved?"
            [(ngModel)]="newQuestionLabel" />
          <textarea class="php-cat-input" rows="2" placeholder="Answers, one per line (blank = Yes / No / Unknown)"
            [(ngModel)]="newQuestionAnswers"></textarea>
          <button type="button" class="php-cat-addbtn" (click)="addQuestion()">Add question</button>
        </div>
      </div>
    </div>
    }
  </div>

  <div drawer-actions class="sw-side-panel-actions">
    <button type="button" class="sw-modal-btn sw-modal-btn--discard" (click)="closePanel()">Discard</button>
    <button type="button" class="sw-modal-btn sw-modal-btn--update" (click)="save()" [disabled]="isSaving">
      @if (isSaving) { <span class="spinner-border spinner-border-sm"></span> }
      Save
    </button>
  </div>
</sw-drawer>
}
